import React from 'react';
import { Global, css } from '@emotion/react';
import { minimalTheme } from '../styles/colors';
import {
  fontFamilies,
  fontWeights,
  breakpoints,
  heroFontFaces,
} from '../styles/typography';

/** @font-face for the hero fonts, plus a metric-matched fallback for each. */
const heroFontFaceCss = heroFontFaces
  .map(face => {
    const style = 'style' in face ? face.style : 'normal';
    const display = 'display' in face ? face.display : 'swap';
    const main = `
    @font-face {
      font-family: '${face.family}';
      src: url('/fonts/${face.file}') format('woff2');
      font-weight: ${face.weight};
      font-style: ${style};
      font-display: ${display};
    }`;
    if (!('fallback' in face)) return main;
    const { fallback } = face;
    return `${main}
    @font-face {
      font-family: '${face.family} Fallback';
      src: local('${fallback.local}');
      font-style: ${style};
      size-adjust: ${fallback.size}%;
      ascent-override: ${fallback.ascent}%;
      descent-override: ${fallback.descent}%;
      line-gap-override: 0%;
    }`;
  })
  .join('\n');

export const GlobalStyles = () => {
  const globalStyles = css`
    ${heroFontFaceCss}

    /* CSS Custom Properties - Theme Variables */
    :root {
      /* Light theme (default) */
      --color-hero-accent: ${minimalTheme.light.heroAccent};
      --color-hero-ink: ${minimalTheme.light.heroInk};
      --color-hero-shade: ${minimalTheme.light.heroShade};
      --color-hero-key: ${minimalTheme.light.heroKey};
      --color-text: ${minimalTheme.light.text};
      --color-text-secondary: ${minimalTheme.light.textSecondary};
      --color-text-tertiary: ${minimalTheme.light.textTertiary};
      --color-heading: ${minimalTheme.light.heading};
      --color-background: ${minimalTheme.light.background};
      --color-background-secondary: ${minimalTheme.light.backgroundSecondary};
      --color-border: ${minimalTheme.light.border};
      --color-accent: ${minimalTheme.light.accent};
      --color-accent-hover: ${minimalTheme.light.accentHover};
      --color-link: ${minimalTheme.common.link};
      --color-link-hover: ${minimalTheme.common.linkHover};
      --color-code-background: ${minimalTheme.light.codeBackground};
      --color-code-text: ${minimalTheme.light.codeText};
      --color-card-background: ${minimalTheme.light.cardBackground};
      --color-card-border: ${minimalTheme.light.cardBorder};
      --color-card-shadow: ${minimalTheme.light.cardShadow};
      --color-inverted-background: ${minimalTheme.light.invertedBackground};
      --color-hover-background: ${minimalTheme.light.hoverBackground};
      --color-selection: ${minimalTheme.common.selection};
    }

    /* System dark mode preference */
    @media (prefers-color-scheme: dark) {
      :root {
        --color-hero-accent: ${minimalTheme.dark.heroAccent};
        --color-hero-ink: ${minimalTheme.dark.heroInk};
        --color-hero-shade: ${minimalTheme.dark.heroShade};
        --color-hero-key: ${minimalTheme.dark.heroKey};
        --color-text: ${minimalTheme.dark.text};
        --color-text-secondary: ${minimalTheme.dark.textSecondary};
        --color-text-tertiary: ${minimalTheme.dark.textTertiary};
        --color-heading: ${minimalTheme.dark.heading};
        --color-background: ${minimalTheme.dark.background};
        --color-background-secondary: ${minimalTheme.dark.backgroundSecondary};
        --color-border: ${minimalTheme.dark.border};
        --color-accent: ${minimalTheme.dark.accent};
        --color-accent-hover: ${minimalTheme.dark.accentHover};
        --color-link: ${minimalTheme.common.link};
        --color-link-hover: ${minimalTheme.common.linkHover};
        --color-code-background: ${minimalTheme.dark.codeBackground};
        --color-code-text: ${minimalTheme.dark.codeText};
        --color-card-background: ${minimalTheme.dark.cardBackground};
        --color-card-border: ${minimalTheme.dark.cardBorder};
        --color-card-shadow: ${minimalTheme.dark.cardShadow};
        --color-inverted-background: ${minimalTheme.dark.invertedBackground};
        --color-hover-background: ${minimalTheme.dark.hoverBackground};
      }
    }

    /* Manual dark theme override (takes precedence over media query) */
    html[data-theme='dark'] {
      --color-hero-accent: ${minimalTheme.dark.heroAccent};
      --color-hero-ink: ${minimalTheme.dark.heroInk};
      --color-hero-shade: ${minimalTheme.dark.heroShade};
      --color-hero-key: ${minimalTheme.dark.heroKey};
      --color-text: ${minimalTheme.dark.text};
      --color-text-secondary: ${minimalTheme.dark.textSecondary};
      --color-text-tertiary: ${minimalTheme.dark.textTertiary};
      --color-heading: ${minimalTheme.dark.heading};
      --color-background: ${minimalTheme.dark.background};
      --color-background-secondary: ${minimalTheme.dark.backgroundSecondary};
      --color-border: ${minimalTheme.dark.border};
      --color-accent: ${minimalTheme.dark.accent};
      --color-accent-hover: ${minimalTheme.dark.accentHover};
      --color-link: ${minimalTheme.common.link};
      --color-link-hover: ${minimalTheme.common.linkHover};
      --color-code-background: ${minimalTheme.dark.codeBackground};
      --color-code-text: ${minimalTheme.dark.codeText};
      --color-card-background: ${minimalTheme.dark.cardBackground};
      --color-card-border: ${minimalTheme.dark.cardBorder};
      --color-card-shadow: ${minimalTheme.dark.cardShadow};
      --color-inverted-background: ${minimalTheme.dark.invertedBackground};
      --color-hover-background: ${minimalTheme.dark.hoverBackground};
    }

    /* Manual light theme override (takes precedence over media query) */
    html[data-theme='light'] {
      --color-hero-accent: ${minimalTheme.light.heroAccent};
      --color-hero-ink: ${minimalTheme.light.heroInk};
      --color-hero-shade: ${minimalTheme.light.heroShade};
      --color-hero-key: ${minimalTheme.light.heroKey};
      --color-text: ${minimalTheme.light.text};
      --color-text-secondary: ${minimalTheme.light.textSecondary};
      --color-text-tertiary: ${minimalTheme.light.textTertiary};
      --color-heading: ${minimalTheme.light.heading};
      --color-background: ${minimalTheme.light.background};
      --color-background-secondary: ${minimalTheme.light.backgroundSecondary};
      --color-border: ${minimalTheme.light.border};
      --color-accent: ${minimalTheme.light.accent};
      --color-accent-hover: ${minimalTheme.light.accentHover};
      --color-link: ${minimalTheme.common.link};
      --color-link-hover: ${minimalTheme.common.linkHover};
      --color-code-background: ${minimalTheme.light.codeBackground};
      --color-code-text: ${minimalTheme.light.codeText};
      --color-card-background: ${minimalTheme.light.cardBackground};
      --color-card-border: ${minimalTheme.light.cardBorder};
      --color-card-shadow: ${minimalTheme.light.cardShadow};
      --color-inverted-background: ${minimalTheme.light.invertedBackground};
      --color-hover-background: ${minimalTheme.light.hoverBackground};
    }

    html {
      box-sizing: border-box;
      -ms-text-size-adjust: 100%;
      -webkit-text-size-adjust: 100%;
      overflow-y: scroll;
      overflow-x: hidden;
      width: 100%;
      max-width: 100vw;

      /* Custom scrollbar for webkit browsers (Chrome, Safari, Edge) */
      &::-webkit-scrollbar {
        width: 6px;
      }
      &::-webkit-scrollbar-track {
        background: transparent;
      }
      &::-webkit-scrollbar-thumb {
        background: rgba(128, 128, 128, 0);
        border-radius: 3px;
        transition: background 0.3s ease;
      }
      &:hover::-webkit-scrollbar-thumb {
        background: rgba(128, 128, 128, 0.3);
      }
      &::-webkit-scrollbar-thumb:hover {
        background: rgba(128, 128, 128, 0.5);
      }

      /* Firefox scrollbar */
      scrollbar-width: thin;
      scrollbar-color: rgba(128, 128, 128, 0.3) transparent;

      @media (max-width: ${breakpoints.mobile}) {
        overflow-y: auto;
        /* Hide scrollbar on mobile */
        scrollbar-width: none; /* Firefox */
        -ms-overflow-style: none; /* IE and Edge */
        &::-webkit-scrollbar {
          display: none; /* Chrome, Safari, Opera */
        }
      }

      transition:
        background-color 0.3s ease,
        color 0.3s ease;
    }
    *,
    *:before,
    *:after {
      box-sizing: inherit;
    }
    body {
      margin: 0;
      padding: 0;
      width: 100%;
      max-width: 100vw;
      overflow-x: hidden;
      font-family: ${fontFamilies.sansSerif};
      background-color: var(--color-background);
      color: var(--color-text);
      line-height: 1.6;
      -webkit-font-smoothing: antialiased;
      -moz-osx-font-smoothing: grayscale;
      transition:
        background-color 0.3s ease,
        color 0.3s ease;
    }

    /* Disable default blue tap highlight on mobile */
    a,
    button,
    [role='button'],
    input,
    select,
    textarea {
      -webkit-tap-highlight-color: transparent;
    }

    a {
      color: inherit;
      text-decoration: underline;
      text-underline-offset: 4px;
    }
    a:hover {
      text-decoration-thickness: 2px;
    }

    /* Reset headings */
    h1,
    h2,
    h3,
    h4,
    h5,
    h6 {
      margin: 0;
      font-family: ${fontFamilies.serif};
      font-weight: ${fontWeights.semibold};
      color: var(--color-heading);
    }

    /* Text selection */
    ::selection {
      background: var(--color-selection);
    }
    ::-moz-selection {
      background: var(--color-selection);
    }
  `;

  return <Global styles={globalStyles} />;
};
